/**
 * useCreateDrag.ts
 * Drag across empty day-cells on a machine row to create an event
 * (Machine Schedule UX — M7). Returns the live selection (for rendering a
 * rectangle in the row) and a pointer-down starter to attach to each row's
 * day-span. On release it calls onCreateAt(machineKey, startISO, endISO).
 *
 * Extracted from MachineScheduleGrid to keep that file under the 600-line cap.
 */

import { useState, useCallback, useRef, useEffect } from 'react';

export interface CreateSelection {
  machineKey: string;
  loIdx: number;
  hiIdx: number;
}

interface UseCreateDragResult {
  createSel: CreateSelection | null;
  /** Attach to a day-span's onPointerDown; ignores presses on a booking bar. */
  beginCreate: (e: React.PointerEvent, machineKey: string) => void;
}

export function useCreateDrag(
  days: string[],
  totalDays: number,
  onCreateAt?: (machineKey: string, startISO: string, endISO: string) => void,
): UseCreateDragResult {
  const createRef = useRef<{
    machineKey: string;
    rectLeft: number;
    rectWidth: number;
    aIdx: number;
    bIdx: number;
  } | null>(null);
  const [createSel, setCreateSel] = useState<CreateSelection | null>(null);

  // Keep day data in refs so the global listeners avoid stale closures.
  const daysRef = useRef(days);
  const totalRef = useRef(totalDays);
  const onCreateRef = useRef(onCreateAt);
  useEffect(() => { daysRef.current = days; }, [days]);
  useEffect(() => { totalRef.current = totalDays; }, [totalDays]);
  useEffect(() => { onCreateRef.current = onCreateAt; }, [onCreateAt]);

  const handleMove = useCallback((e: PointerEvent) => {
    const c = createRef.current;
    if (!c) return;
    const total = totalRef.current;
    const idx = Math.max(
      0,
      Math.min(total - 1, Math.floor(((e.clientX - c.rectLeft) / c.rectWidth) * total)),
    );
    c.bIdx = idx;
    setCreateSel({ machineKey: c.machineKey, loIdx: Math.min(c.aIdx, idx), hiIdx: Math.max(c.aIdx, idx) });
  }, []);

  const handleUp = useCallback(() => {
    window.removeEventListener('pointermove', handleMove);
    window.removeEventListener('pointerup', handleUp);
    const c = createRef.current;
    createRef.current = null;
    setCreateSel(null);
    if (c && onCreateRef.current) {
      const d = daysRef.current;
      onCreateRef.current(c.machineKey, d[Math.min(c.aIdx, c.bIdx)], d[Math.max(c.aIdx, c.bIdx)]);
    }
  }, [handleMove]);

  const beginCreate = useCallback(
    (e: React.PointerEvent, machineKey: string) => {
      if (!onCreateRef.current) return;
      if ((e.target as HTMLElement).closest('[data-booking-key]')) return; // empty space only
      const rect = e.currentTarget.getBoundingClientRect();
      const total = totalRef.current;
      const idx = Math.max(
        0,
        Math.min(total - 1, Math.floor(((e.clientX - rect.left) / rect.width) * total)),
      );
      createRef.current = { machineKey, rectLeft: rect.left, rectWidth: rect.width, aIdx: idx, bIdx: idx };
      setCreateSel({ machineKey, loIdx: idx, hiIdx: idx });
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
    },
    [handleMove, handleUp],
  );

  useEffect(
    () => () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    },
    [handleMove, handleUp],
  );

  return { createSel, beginCreate };
}
